'use client';
import { CloseOutlined, ShoppingOutlined } from '@ant-design/icons';
import { DataContextCart } from '@root/src/contextcart';
import Image from '@components/common/ImageFallback';
import React, { useContext, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslation } from '@root/src/i18n/client';
import { convertCurrency } from '@lib/format';
import Badge from '@root/src/components/common/Bag';

const Drawer: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const drawerContentRef = useRef(null);
  const { t } = useTranslation('menu');
  const { t: tCommon } = useTranslation('common');
  const router = useRouter();
  const pathname = usePathname();
  const contextcart: any = useContext(DataContextCart);
  const { dataCart = [], updateCart } = contextcart || {};

  const getCountNumber = () => {
    return dataCart.reduce((accumulator, item) => {
      return accumulator + item.quantity;
    }, 0);
  };
  const getSubTotal = () => {
    const total = dataCart.reduce((accumulator, item) => {
      const itemTotal =
        item.quantity *
        Number(item.productPrice || item.retailPrice || 0) *
        (1 - (item.discount || 0) / 100);
      return accumulator + itemTotal;
    }, 0);
    return total;
  };

  const getItemPrice = (item: any) => Number(item.productPrice || item.retailPrice || 0);
  const getItemName = (item: any) =>
    item.productName || item.name || tCommon('lytra.productFallbackName');
  const getItemImage = (item: any) =>
    typeof item.productImage === 'string' && item.productImage.trim()
      ? item.productImage
      : '/images/placeholder-tea.jpg';
  const getLineTotal = (item: any) =>
    item.quantity * getItemPrice(item) * (1 - (item.discount || 0) / 100);

  const showDrawer = () => {
    setVisible(true);
  };
  const onClose = () => {
    setVisible(false);
  };

  React.useEffect(() => {
    setVisible(false);
  }, [pathname]);

  const deleteCartItem = (id: string) => {
    let localCart = [...dataCart];
    localCart = localCart?.filter((item: any) => item.productId !== id);
    updateCart?.(localCart);
  };

  const updateItemQuantity = (id: string, nextQuantity: number) => {
    if (nextQuantity <= 0) {
      deleteCartItem(id);
      return;
    }

    let currentCart = [...dataCart];
    currentCart = currentCart.map((cartItem: any) =>
      cartItem.productId === id
        ? { ...cartItem, quantity: nextQuantity }
        : cartItem
    );
    updateCart?.(currentCart);
  };

  const handleCheckout = () => {
    setVisible(false);
    router.push('/cart');
  };
  return (
    <>
      <Badge count={getCountNumber()}>
        <div>
          <ShoppingOutlined
            onMouseEnter={showDrawer}
            className="text-xl cursor-pointer"
          />
        </div>
      </Badge>
      <div
        className={`fixed top-0 right-0 sm:w-5/12 w-full h-full bg-white shadow-lg z-50 transform ${
          visible ? 'translate-x-0' : 'translate-x-full'
        } transition-transform duration-300 ease-in-out flex flex-col`}
      >
        <div className="border-b border-[#e7dcc8] bg-[#fffaf4] p-5 flex justify-between items-center">
          <h2 className="text-[2rem] font-serif text-[#4d463b]">{t(`CART`)}</h2>
          <button onClick={onClose} className="text-[#6f675b] transition hover:text-[#223A27]">
            <CloseOutlined />
          </button>
        </div>
        <div ref={drawerContentRef} className="flex-grow overflow-y-auto bg-[#fcf8f1] p-4">
          {dataCart.length == 0 ? (
            <p className="rounded-[24px] border border-dashed border-[#d9ccb6] bg-white p-6 text-center text-[20px] font-semibold text-[#857b6d]">
              {t('cartEmpty')}
            </p>
          ) : (
            dataCart.map((item, index) => (
              <div
                className="mb-4 rounded-[28px] border border-[#e7dcc8] bg-white p-4 shadow-[0_14px_30px_rgba(91,77,56,0.05)]"
                key={index}
              >
                <div className="flex items-start space-x-4">
                <Image
                  width={100}
                  height={100}
                  src={getItemImage(item)}
                  alt="Product Image"
                  className="h-28 w-28 rounded-[20px] object-cover"
                />
                <div className="flex flex-col flex-grow">
                  <div className="flex justify-between">
                    <div>
                      <h2 className="mb-3 w-full text-start text-lg font-medium text-[#4d463b] line-clamp-2">
                        {getItemName(item)}
                      </h2>
                      <div className="flex items-center gap-3">
                        <div className="inline-flex items-center rounded-full border border-[#d8ccb8] bg-[#fffaf4]">
                          <button
                            type="button"
                            onClick={() =>
                              updateItemQuantity(item.productId, Number(item.quantity || 0) - 1)
                            }
                            className="flex h-10 w-10 items-center justify-center text-[#5a5247] transition hover:bg-[#f5efe5]"
                          >
                            -
                          </button>
                          <span className="flex h-10 min-w-[44px] items-center justify-center border-x border-[#e8ddcb] px-3 font-semibold text-[#4d463b]">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              updateItemQuantity(item.productId, Number(item.quantity || 0) + 1)
                            }
                            className="flex h-10 w-10 items-center justify-center text-[#5a5247] transition hover:bg-[#f5efe5]"
                          >
                            +
                          </button>
                        </div>
                        <div className="text-sm text-[#776d5f]">
                          x {convertCurrency(getItemPrice(item))}
                        </div>
                      </div>
                      <p className="mt-3 text-start text-lg font-semibold text-[#223A27]">
                        {convertCurrency(getLineTotal(item))}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteCartItem(item.productId)}
                      className="text-[#8a8174] transition hover:cursor-pointer hover:text-[#223A27]"
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                </div>
              </div>
              </div>
            ))
          )}
        </div>
        <div className="border-t-[4px] bg-white pt-4 mt-auto p-4" style={{ borderTopColor: '#223A27' }}>
          <button
            disabled={getCountNumber() == 0}
            className="flex w-full items-center justify-between rounded-[18px] border border-[#223A27] bg-[#223A27] px-5 py-4 text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleCheckout}
          >
            <span className="flex flex-col items-start leading-tight text-left">
              <span className="text-xs uppercase tracking-[0.12em] text-white/75 sm:text-sm">
                {t('Total')}
              </span>
              <span className="mt-1 text-lg font-semibold sm:text-xl">
                {dataCart.length === 0 ? '0' : convertCurrency(getSubTotal())}
              </span>
            </span>
            <span className="font-bold text-lg">{t('Checkout')}</span>
          </button>
        </div>
      </div>
    </>
  );
};
export default Drawer;
